import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Chat } from '../types/chat';
import { orchestratorApi } from '../api/orchestrator';
import ChatMessages from './ChatMessages';
import ModelSettings from './ModelSettings';
import { MemoryReviewPanel } from './MemoryReviewPanel';
import { normalizePhoneNumber, formatPhoneNumber } from '../utils/phoneNormalization';
import { useTenantId } from '../auth/tenantContext';

const getPreferredChatName = (chat: Chat) => {
  // Use DB-persisted name (resolved via CRM/LLM resolver). Fall back to phone number —
  // never use derived_client_name here as it produces too many false positives.
  if (chat.name && !chat.name.startsWith('+')) return chat.name;
  return formatPhoneNumber(normalizePhoneNumber(chat.phone_number));
};

const getPreferredChatPhone = (chat: Chat) => chat.derived_phone_number || chat.phone_number;

// Utility function to format chat list timestamps.
// Always shows both date and time in the selected entity timezone.
const formatDateTime = (timestamp: string | number | Date, timezone?: string) => {
  try {
    const date = new Date(timestamp);
    const tz = timezone || 'Asia/Singapore';

    return date.toLocaleString('en-GB', {
      timeZone: tz,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

// Utility function to format date/time with timezone for summary modal
const formatDateTimeWithTimezone = (timestamp: string | number | Date, timezone?: string) => {
  try {
    const date = new Date(timestamp);
    const tz = timezone || 'Asia/Singapore'; // Default to Singapore if no timezone specified

    return date.toLocaleString('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting date with timezone:', error);
    return new Date(timestamp).toLocaleString();
  }
};

interface ChatBoardProps {
  chats: Chat[];
  wsStatus: string;
  onRefreshChats: () => void;
  onRefreshChatsWithEntityId?: (entityId: string) => void;
  hasMoreChats?: boolean;
  loadingMore?: boolean;
  onLoadMoreChats?: () => void;
  onLoadAllChats?: () => void;
  onLoadChatsWithLimit?: (limit: number) => void;
  selectedEntityId?: string;
  onSelectedEntityChange?: (entityId: string) => void; // Re-added for user entity selection only
}

interface Entity {
  id: string;
  name: string;
  phone: string;
  status: 'connected' | 'disconnected';
  timezone?: string;
  tenantId?: string;
}

type WorkerStatus = 'checking' | 'online' | 'offline';
type WhatsAppSessionStatus = 'checking' | 'connected' | 'reconnecting' | 'qr_required' | 'disconnected';

interface ClassifyResult {
  id: number;
  chat_id: string;
  phone_number: string;
  classification: string;
  confidence_pct: number;
  summary: string;
  reasoning: string;
  zoho_lead_id?: string;
  saved_to_crm: boolean;
}

interface BroadcastHistoryEntry {
  recipient_id: number;
  campaign_id: number;
  campaign_name: string;
  message: string;
  phone_number: string;
  recipient_name?: string;
  sent_at: string;
  status: string;
  message_id?: string;
}

const CLASSIFY_STATUSES = [
  'Contact in Future',
  'Junk Lead',
  'Lost Lead',
  'Contacted - Low (1)',
  'Contacted - Mid (2)',
  'Contacted - High (3)',
  'Appt Fixed (4)',
];

// Convert a Date to the local-time string expected by datetime-local inputs (YYYY-MM-DDTHH:MM).
// Using toISOString() gives UTC which shifts the displayed time by the user's timezone offset.
const toLocalDatetimeStr = (date: Date): string => {
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const ChatBoard: React.FC<ChatBoardProps> = ({
  chats,
  // @ts-ignore - unused but part of interface
  wsStatus,
  onRefreshChats,
  onRefreshChatsWithEntityId,
  // @ts-ignore - unused but part of interface
  hasMoreChats,
  loadingMore,
  // @ts-ignore - unused but part of interface
  onLoadMoreChats,
  onLoadAllChats,
  onLoadChatsWithLimit,
  selectedEntityId: propSelectedEntityId,
  onSelectedEntityChange, // Re-added parameter
}) => {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [workerStatus, setWorkerStatus] = useState<WorkerStatus>('checking');
  const [whatsappSessionStatus, setWhatsAppSessionStatus] = useState<WhatsAppSessionStatus>('checking');
  // Initialize with prop value or saved entity ID from localStorage
  const [selectedEntityId, setSelectedEntityId] = useState<string>(() => {
    const localStorageEntityId = localStorage.getItem('selectedEntityId');
    const initialEntityId = propSelectedEntityId || localStorageEntityId || '';

    console.log('[ChatBoard] ChatBoard component mounting - Initial entity:', {
      propSelectedEntityId,
      localStorageEntityId,
      initialEntityId
    });

    return initialEntityId;
  });

  // Use refs to store stable callback references (prevents duplicate event listeners)
  const onRefreshChatsRef = useRef(onRefreshChats);
  const onRefreshChatsWithEntityIdRef = useRef(onRefreshChatsWithEntityId);

  useEffect(() => {
    onRefreshChatsRef.current = onRefreshChats;
    onRefreshChatsWithEntityIdRef.current = onRefreshChatsWithEntityId;
  }, [onRefreshChats, onRefreshChatsWithEntityId]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<any>(null);
  const [chatSearchTerm, setChatSearchTerm] = useState('');
  const [messageSearchTerm, setMessageSearchTerm] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [leadStatusFilter, setLeadStatusFilter] = useState('');
  const [leadDateFrom, setLeadDateFrom] = useState('');
  const [leadDateTo, setLeadDateTo] = useState('');
  const [availableProducts, setAvailableProducts] = useState<string[]>([]);
  const [broadcastHistory, setBroadcastHistory] = useState<BroadcastHistoryEntry[]>([]);
  const [loadingBroadcastHistory, setLoadingBroadcastHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSyncDropdown, setShowSyncDropdown] = useState(false);
  const [showLoadDropdown, setShowLoadDropdown] = useState(false);
  const [selectedLoadLimit, setSelectedLoadLimit] = useState(100);
  const [showMemoryPanel, setShowMemoryPanel] = useState(false);
  const [aiPauseStatus, setAiPauseStatus] = useState<{
    ai_paused: boolean;
    ai_paused_until: string | null;
    last_human_intervention_at: string | null;
    minutes_remaining: number;
  } | null>(null);

  // Chat summary state
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showSummarySettings, setShowSummarySettings] = useState(false);
  const [summaryStartTime, setSummaryStartTime] = useState('');
  const [summaryEndTime, setSummaryEndTime] = useState('');
  const [summaries, setSummaries] = useState<any[]>([]);
  const [loadingSummaries, setLoadingSummaries] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [pushingToZoho, setPushingToZoho] = useState(false);
  const [zohoPushResult, setZohoPushResult] = useState<{
    successCount: number;
    failedCount: number;
    noLeadCount: number;
    results: any[];
  } | null>(null);

  // Unanswered messages state
  const [showUnansweredModal, setShowUnansweredModal] = useState(false);
  const [showUnansweredConfirmModal, setShowUnansweredConfirmModal] = useState(false);
  const [maxChatsInput, setMaxChatsInput] = useState(100);
  const [minutesThresholdInput, setMinutesThresholdInput] = useState(30);
  const [unansweredChats, setUnansweredChats] = useState<any[]>([]);
  const [loadingUnanswered, setLoadingUnanswered] = useState(false);
  const [unansweredProgress, setUnansweredProgress] = useState<{current: number; total: number; message: string} | null>(null);
  const [unansweredLlmModel, setUnansweredLlmModel] = useState<string | null>(null);
  const [selectedChatIds, setSelectedChatIds] = useState<Set<string>>(new Set());
  const [triggeredChatIds, setTriggeredChatIds] = useState<Set<string>>(new Set());
  const [respondStatusMessage, setRespondStatusMessage] = useState<string | null>(null);
  const [chatAgents, setChatAgents] = useState<any[]>([]);
  const [selectedChatAgentId, setSelectedChatAgentId] = useState('');
  const [respondingToChats, setRespondingToChats] = useState(false);
  const [chatMessagesCache, setChatMessagesCache] = useState<Record<string, any[]>>({});
  const [collapsedChatIds, setCollapsedChatIds] = useState<Set<string>>(new Set());
  const [loadingMessagesChatId, setLoadingMessagesChatId] = useState<string | null>(null);
  const [currentScanId, setCurrentScanId] = useState<string | null>(null);
  const [refreshingMessages, setRefreshingMessages] = useState(false);

  // Track user's explicit chat selection from left panel (for scroll-to-bottom behavior)
  const [userExplicitlySelectedChat, setUserExplicitlySelectedChat] = useState(false);

  // Create Group state
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupParticipants, setNewGroupParticipants] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupCreationError, setGroupCreationError] = useState<string | null>(null);

  // Lead classification state
  const [showClassifyModal, setShowClassifyModal] = useState(false);
  const [classifyLoading, setClassifyLoading] = useState(false);
  const [classifyResults, setClassifyResults] = useState<ClassifyResult[]>([]);
  const [classifyOverrides, setClassifyOverrides] = useState<Record<number, string>>({});
  const [savingToCrm, setSavingToCrm] = useState(false);
  const [classifySaveStatus, setClassifySaveStatus] = useState<{ saved: number; failed: number; errors: string[] } | null>(null);
  const [classifyStartDate, setClassifyStartDate] = useState<string>(() => toLocalDatetimeStr(new Date(Date.now() - 72 * 60 * 60 * 1000)));
  const [classifyEndDate, setClassifyEndDate] = useState<string>(() => toLocalDatetimeStr(new Date()));
  const [classifyRunErrors, setClassifyRunErrors] = useState<string[]>([]);
  const [classifyHasRun, setClassifyHasRun] = useState(false);

  // Zoho lead status state - Map of chat_id -> lead info
  const [zohoLeadInfoCache, setZohoLeadInfoCache] = useState<Map<string, {
    lead_status?: string;
    response_mode?: 'normal' | 'light' | 'none';
    loading: boolean;
    fetched_at?: number;
    lookup_phone?: string;
  }>>(new Map());

  // Fetch Zoho lead info for a specific phone number (with caching)
  const fetchZohoLeadInfo = async (chat: Chat) => {
    const phoneNumber = getPreferredChatPhone(chat);
    const chatId = chat.chat_id;
    // Normalize phone number: strip '+' and whitespace for Zoho matching
    const normalizedPhone = normalizePhoneNumber(phoneNumber);
    // Fetch Zoho lead info for phone number

    // Check cache first (5 minute TTL)
    const cached = zohoLeadInfoCache.get(chatId);
    if (
      cached &&
      cached.lookup_phone === normalizedPhone &&
      cached.fetched_at &&
      Date.now() - cached.fetched_at < 300000
    ) {
      // Using cached data
      return cached;
    }

    // Set loading state
    setZohoLeadInfoCache(prev => new Map(prev).set(chatId, {
      loading: true,
      lookup_phone: normalizedPhone
    }));

    try {
      // Send normalized phone (no '+', no spaces) to API for Zoho matching
      const params = new URLSearchParams({
        phone_number: normalizedPhone,
        chat_id: chat.chat_id
      });
      if (selectedEntityId) params.set('entity_id', selectedEntityId);
      const contactName = chat.derived_client_name || chat.name;
      if (contactName) params.set('contact_name', contactName);
      const url = `/api/admin/zoho/response-control/lead-info?${params.toString()}`;

      const response = await fetch(url, {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok && data.lead_info && data.lead_info.lead_status) {
        const leadStatus = data.lead_info.lead_status;
        let responseMode: 'normal' | 'light' | 'none' = 'normal';

        if (leadStatus === 'None' || leadStatus === 'Attempted to Contact') {
          responseMode = 'light';
        } else if (leadStatus === 'Junk Lead' || leadStatus === 'Lost Lead') {
          responseMode = 'none';
        }

        const leadInfo = {
          lead_status: leadStatus,
          response_mode: responseMode,
          loading: false,
          fetched_at: Date.now(),
          lookup_phone: normalizedPhone
        };

        setZohoLeadInfoCache(prev => new Map(prev).set(chatId, leadInfo));
        return leadInfo;
      } else {
        const noInfo = { loading: false, fetched_at: Date.now(), lookup_phone: normalizedPhone };
        setZohoLeadInfoCache(prev => new Map(prev).set(chatId, noInfo));
        return noInfo;
      }
    } catch (error) {
      console.error('❌ [Zoho Badge Debug] Failed to fetch Zoho lead info:', error);
      const errorInfo = { loading: false, fetched_at: Date.now(), lookup_phone: normalizedPhone };
      setZohoLeadInfoCache(prev => new Map(prev).set(chatId, errorInfo));
      return errorInfo;
    }
  };

  const handleDetectedChatIdentity = useCallback((identity: {
    derived_phone_number?: string | null;
    derived_client_name?: string | null;
  }) => {
    if (!selectedChat || (!identity.derived_phone_number && !identity.derived_client_name)) {
      return;
    }

    const newPhone = identity.derived_phone_number || selectedChat.derived_phone_number || null;
    const newName = identity.derived_client_name || selectedChat.derived_client_name || null;

    // Only update state if something actually changed — prevents render loops
    if (newPhone === selectedChat.derived_phone_number && newName === selectedChat.derived_client_name) {
      return;
    }

    const nextChat: Chat = {
      ...selectedChat,
      derived_phone_number: newPhone,
      derived_client_name: newName,
    };

    setSelectedChat(nextChat);

    if (!nextChat.is_group && getPreferredChatPhone(nextChat)) {
      fetchZohoLeadInfo(nextChat);
    }
  }, [selectedChat, selectedEntityId]); // zohoLeadInfoCache removed — fetchZohoLeadInfo always reads latest via closure

  // ESC key handler for closing modals
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showSummaryModal) {
          setShowSummaryModal(false);
        } else if (showSummarySettings) {
          setShowSummarySettings(false);
        } else if (showUnansweredModal) {
          setShowUnansweredModal(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showSummaryModal, showSummarySettings, showUnansweredModal]);

  const { user } = useAuth0();
  const authTenantId = useTenantId();

  // Helper to format date for datetime-local input (YYYY-MM-DDTHH:mm)
  const formatForDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Chat summary helper functions
  const setWorkDayTime = () => {
    const today = new Date();
    const start = new Date(today);
    start.setHours(9, 0, 0, 0);
    const end = new Date(today);
    end.setHours(18, 0, 0, 0);
    setSummaryStartTime(formatForDateTimeLocal(start));
    setSummaryEndTime(formatForDateTimeLocal(end));
  };

  const setAfterHoursTime = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const start = new Date(yesterday);
    start.setHours(18, 0, 0, 0);
    const end = new Date(today);
    end.setHours(9, 0, 0, 0);
    setSummaryStartTime(formatForDateTimeLocal(start));
    setSummaryEndTime(formatForDateTimeLocal(end));
  };

  const fetchSummaries = async () => {
    if (!selectedEntityId) {
      setSummaryError('Please select an entity first');
      return;
    }

    if (!summaryStartTime || !summaryEndTime) {
      setSummaryError('Please select start and end times');
      return;
    }

    setLoadingSummaries(true);
    setSummaryError(null);

    try {
      const result = await orchestratorApi.summarizeChats(
        selectedEntityId,
        new Date(summaryStartTime).toISOString(),
        new Date(summaryEndTime).toISOString(),
        false
      );

      if (result.error) {
        setSummaryError(result.error);
      } else {
        setSummaries(result.summaries);
        setShowSummarySettings(false);
        setShowSummaryModal(true);
      }
    } catch (error: any) {
      setSummaryError(error.message || 'Failed to generate summaries');
    } finally {
      setLoadingSummaries(false);
    }
  };

  const handlePushToZoho = async () => {
    if (!selectedEntityId) return;
    setPushingToZoho(true);
    setZohoPushResult(null);

    try {
      const result = await orchestratorApi.pushSummariesToZoho(
        selectedEntityId,
        new Date(summaryStartTime).toISOString(),
        new Date(summaryEndTime).toISOString()
      );

      if (!result.ok && result.error) {
        setSummaryError(result.error);
      } else {
        setZohoPushResult({
          successCount: result.successCount,
          failedCount: result.failedCount,
          noLeadCount: result.noLeadCount,
          results: result.results,
        });

        // Update local summaries with zoho push status
        setSummaries(prev => prev.map(s => {
          const pushed = result.results.find((r: any) => r.chatId === s.chatId);
          if (!pushed) return s;
          return {
            ...s,
            zohoPushedAt: pushed.status === 'success' ? new Date().toISOString() : s.zohoPushedAt,
            zohoPushStatus: pushed.status === 'success' ? 'success' : pushed.status === 'failed' ? 'failed' : s.zohoPushStatus,
            zohoLeadId: pushed.zohoLeadId || s.zohoLeadId,
          };
        }));
      }
    } catch (error: any) {
      setSummaryError(error.message || 'Failed to push to CRM');
    } finally {
      setPushingToZoho(false);
    }
  };

  // Unanswered messages handlers
  const checkUnansweredMessages = async (maxChats: number, minutesThreshold: number) => {
    if (!selectedEntityId) {
      alert('Please select an entity first');
      return;
    }

    setShowUnansweredConfirmModal(false);
    setShowUnansweredModal(true);
    setLoadingUnanswered(true);
    setUnansweredChats([]);
    setSelectedChatIds(new Set());
    setTriggeredChatIds(new Set());
    setRespondStatusMessage(null);
    setChatMessagesCache({});
    setCollapsedChatIds(new Set());
    setCurrentScanId(null);

    const selectedEntity = entities.find(e => e.id === selectedEntityId);
    if (!selectedEntity) {
      alert('Could not find entity information');
      setShowUnansweredModal(false);
      setLoadingUnanswered(false);
      return;
    }

    const whatsappEntityId = selectedEntity.id;
    const totalBatches = Math.ceil(maxChats / 50);
    let offset = 0;
    let batchNum = 0;
    let totalChecked = 0;
    let hasMore = true;
    let accumulatedResults: any[] = [];
    let batchErrors = 0;

    try {
      await loadChatAgents();

      while (hasMore && totalChecked < maxChats) {
        batchNum++;
        const batchEnd = Math.min(totalChecked + 50, maxChats);
        setUnansweredProgress({
          current: totalChecked,
          total: maxChats,
          message: `Batch ${batchNum}/${totalBatches}: checking chats ${totalChecked + 1}–${batchEnd}...`
        });

        try {
          const response = await fetch(
            `/api/unanswered/entity-assignments/${whatsappEntityId}/unanswered-chats?maxChats=50&offset=${offset}&minutesThreshold=${minutesThreshold}`,
            { headers: await orchestratorApi.getAuthHeaders() }
          );

          if (response.status === 404) {
            const data = await response.json();
            if (data.error?.includes('not enabled')) {
              setShowUnansweredModal(false);
              alert('Unanswered messages monitor is not enabled for this entity. Please enable it in Agent Controls → Unanswered Messages Monitor.');
              return;
            }
          }

          if (!response.ok) {
            batchErrors++;
            setUnansweredProgress({
              current: totalChecked,
              total: maxChats,
              message: `⚠️ Batch ${batchNum} failed (${batchErrors} error${batchErrors > 1 ? 's' : ''} so far) — continuing...`
            });
            offset += 50;
            totalChecked += 50;
            continue;
          }

          const data = await response.json();

          if (data.ok && data.unansweredChats) {
            if (batchNum === 1) setUnansweredLlmModel(data.entity?.llmModel || null);
            accumulatedResults = [...accumulatedResults, ...data.unansweredChats];
            setUnansweredChats([...accumulatedResults]);
            // Pre-populate messages cache from API response
            setChatMessagesCache(prev => {
              const next = { ...prev };
              for (const chat of data.unansweredChats) {
                if (chat.recentMessages && chat.recentMessages.length > 0) {
                  next[chat.chatId] = chat.recentMessages;
                }
              }
              return next;
            });
            totalChecked += data.chatsChecked || 50;
            hasMore = data.hasMore;
            offset += 50;

            setUnansweredProgress({
              current: totalChecked,
              total: maxChats,
              message: `Batch ${batchNum}/${totalBatches} done — ${accumulatedResults.length} unanswered found so far`
            });
          } else {
            batchErrors++;
            offset += 50;
            totalChecked += 50;
          }
        } catch (batchError: any) {
          batchErrors++;
          console.error(`Batch ${batchNum} error:`, batchError);
          setUnansweredProgress({
            current: totalChecked,
            total: maxChats,
            message: `⚠️ Batch ${batchNum} error: ${batchError.message || 'network error'} — continuing...`
          });
          offset += 50;
          totalChecked += 50;
        }
      }

      setUnansweredProgress({
        current: maxChats,
        total: maxChats,
        message: batchErrors > 0
          ? `✅ Done (${batchErrors} batch${batchErrors > 1 ? 'es' : ''} failed) — ${accumulatedResults.length} unanswered found`
          : `✅ Complete — ${accumulatedResults.length} unanswered found`
      });

      // Save scan result for archive
      try {
        const authHeaders = await orchestratorApi.getAuthHeaders();
        const saveRes = await fetch(`/api/unanswered/entity-assignments/${encodeURIComponent(selectedEntityId)}/save-scan`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            totalScanned: totalChecked,
            unansweredCount: accumulatedResults.length,
            minutesThreshold: minutesThresholdInput,
            chats: accumulatedResults.map(c => ({
              chatId: c.chatId,
              chatName: c.chatName,
              phoneNumber: c.phoneNumber,
              questions: c.questions,
              triggered: false,
            })),
          }),
        });
        const saveData = await saveRes.json();
        if (saveData.ok) setCurrentScanId(saveData.scanId);
      } catch (saveErr) {
        console.warn('Failed to save scan to archive:', saveErr);
      }

    } catch (error: any) {
      console.error('Failed to check unanswered messages:', error);
      setUnansweredProgress({
        current: totalChecked,
        total: maxChats,
        message: `❌ Error: ${error.message || 'unexpected failure'}`
      });
    } finally {
      setLoadingUnanswered(false);
    }
  };

  const loadChatAgents = async () => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const selectedEntity = entities.find(e => e.id === selectedEntityId);

      if (!selectedEntity || !selectedEntity.tenantId) {
        console.error('No tenant ID available for selected entity');
        return;
      }

      const response = await fetch(`/api/chat-agents?tenantId=${encodeURIComponent(selectedEntity.tenantId)}`, {
        headers: authHeaders
      });

      const data = await response.json();

      if (data.ok && data.agents) {
        setChatAgents(data.agents);
        if (data.agents.length > 0) {
          setSelectedChatAgentId(data.agents[0].id);
        }
      } else {
        console.error('Failed to load chat agents:', data.error);
      }
    } catch (error) {
      console.error('Failed to load chat agents:', error);
    }
  };

  const toggleChatSelection = (chatId: string) => {
    const newSelection = new Set(selectedChatIds);
    if (newSelection.has(chatId)) {
      newSelection.delete(chatId);
    } else {
      newSelection.add(chatId);
    }
    setSelectedChatIds(newSelection);
  };

  const toggleAllChats = () => {
    // If anything is selected → clear all; if nothing selected → select all
    if (selectedChatIds.size > 0) {
      setSelectedChatIds(new Set());
    } else {
      const selectableIds = unansweredChats
        .filter(chat => !triggeredChatIds.has(chat.chatId))
        .map(chat => chat.chatId);
      setSelectedChatIds(new Set(selectableIds));
    }
  };

  const respondToUnansweredChats = async () => {
    if (selectedChatIds.size === 0) {
      setRespondStatusMessage('⚠️ Please select at least one chat to respond to.');
      return;
    }

    if (!selectedChatAgentId) {
      setRespondStatusMessage('⚠️ Please select an AI agent before responding.');
      return;
    }

    setRespondingToChats(true);
    setRespondStatusMessage(null);

    try {
      const selectedEntity = entities.find(e => e.id === selectedEntityId);
      if (!selectedEntity) {
        setRespondStatusMessage('⚠️ Could not find entity information.');
        return;
      }

      const whatsappEntityId = selectedEntity.id;
      const chatIdsArray = Array.from(selectedChatIds);

      const response = await fetch(`/api/unanswered/entity-assignments/${whatsappEntityId}/respond`, {
        method: 'POST',
        headers: {
          ...await orchestratorApi.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chatIds: chatIdsArray,
          chatAgentId: selectedChatAgentId
        })
      });

      const data = await response.json();

      if (data.ok) {
        const triggeredResults = data.results?.filter((r: any) => r.status === 'triggered') || [];
        const failedResults = data.results?.filter((r: any) => r.status === 'failed') || [];
        const newTriggered = new Set([...triggeredChatIds, ...triggeredResults.map((r: any) => r.chatId)]);
        setTriggeredChatIds(newTriggered);
        // Deselect the ones we just triggered
        setSelectedChatIds(prev => {
          const next = new Set(prev);
          triggeredResults.forEach((r: any) => next.delete(r.chatId));
          return next;
        });
        if (failedResults.length > 0) {
          setRespondStatusMessage(`✅ Triggered ${triggeredResults.length} chat${triggeredResults.length !== 1 ? 's' : ''}. ⚠️ ${failedResults.length} failed — select them and try again.`);
        } else {
          setRespondStatusMessage(`✅ AI response triggered for ${triggeredResults.length} chat${triggeredResults.length !== 1 ? 's' : ''}. Use 🔄 Refresh to see AI replies.`);
        }
        // Update scan archive with triggered chats
        const triggeredChatIdsList = triggeredResults.map((r: any) => r.chatId);
        if (triggeredChatIdsList.length > 0 && currentScanId) {
          try {
            const authHeaders = await orchestratorApi.getAuthHeaders();
            await fetch(`/api/unanswered/entity-assignments/${encodeURIComponent(selectedEntityId)}/save-scan/${currentScanId}/mark-triggered`, {
              method: 'PATCH',
              headers: { ...authHeaders, 'Content-Type': 'application/json' },
              body: JSON.stringify({ triggeredChatIds: triggeredChatIdsList }),
            });
          } catch (e) { console.warn('Failed to update scan archive with triggered chats', e); }
        }
      } else {
        setRespondStatusMessage(`❌ ${data.error || 'Failed to trigger AI responses'}`);
      }
    } catch (error: any) {
      console.error('Failed to respond to unanswered chats:', error);
      setRespondStatusMessage('❌ Failed to trigger AI responses. Please try again.');
    } finally {
      setRespondingToChats(false);
    }
  };

  const refreshTriggeredMessages = async () => {
    if (triggeredChatIds.size === 0) return;
    setRefreshingMessages(true);
    try {
      await Promise.all(
        Array.from(triggeredChatIds).map(cId => loadChatMessages(cId, true))
      );
    } finally {
      setRefreshingMessages(false);
    }
  };

  const loadChatMessages = async (chatId: string, forceRefresh = false) => {
    if (!forceRefresh && chatMessagesCache[chatId] && chatMessagesCache[chatId].length > 0) return;
    setLoadingMessagesChatId(chatId);
    try {
      const selectedEntity = entities.find(e => e.id === selectedEntityId);
      const entityId = selectedEntity?.id || selectedEntityId;
      const res = await fetch(`/api/chats/${encodeURIComponent(chatId)}/messages?limit=20&entityId=${encodeURIComponent(entityId)}`, {
        headers: await orchestratorApi.getAuthHeaders()
      });
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        setChatMessagesCache(prev => ({ ...prev, [chatId]: data.messages }));
      }
    } catch (e) {
      console.error('Failed to load messages for', chatId, e);
    } finally {
      setLoadingMessagesChatId(null);
    }
  };

  // Note: messages are pre-fetched in the batch API response via recentMessages field.
  // loadChatMessages is used only for post-respond refresh (forceRefresh=true).

  // Create group handler
  const handleCreateGroup = async () => {
    if (!selectedEntityId) {
      setGroupCreationError('Please select an entity first');
      return;
    }

    if (!newGroupName.trim()) {
      setGroupCreationError('Please enter a group name');
      return;
    }

    if (!newGroupParticipants.trim()) {
      setGroupCreationError('Please enter at least one participant phone number');
      return;
    }

    // Parse participants (comma or newline separated)
    const participantNumbers = newGroupParticipants
      .split(/[,\n]/)
      .map(num => num.trim())
      .filter(num => num.length > 0);

    if (participantNumbers.length === 0) {
      setGroupCreationError('Please enter at least one valid participant phone number');
      return;
    }

    setCreatingGroup(true);
    setGroupCreationError(null);

    try {
      const result = await orchestratorApi.createGroup(
        newGroupName.trim(),
        participantNumbers,
        selectedEntityId
      );

      if (result.ok) {
        alert(`Group "${result.groupName}" created successfully with ${result.participantCount} participants!`);
        setShowCreateGroupModal(false);
        setNewGroupName('');
        setNewGroupParticipants('');
        // Refresh chats to show the new group
        if (onRefreshChatsWithEntityId) {
          onRefreshChatsWithEntityId(selectedEntityId);
        }
      } else {
        setGroupCreationError(result.error || 'Failed to create group');
      }
    } catch (error: any) {
      setGroupCreationError(error.message || 'Failed to create group. Please try again.');
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleClassifyLeads = () => {
    setShowClassifyModal(true);
    setClassifyResults([]);
    setClassifyOverrides({});
    setClassifySaveStatus(null);
    setClassifyRunErrors([]);
    setClassifyHasRun(false);
  };

  const runClassification = async () => {
    setClassifyLoading(true);
    setClassifyResults([]);
    setClassifyOverrides({});
    setClassifySaveStatus(null);
    setClassifyRunErrors([]);
    setClassifyHasRun(true);
    try {
      const resp = await fetch('/api/admin/lead-classify/run', {
        method: 'POST',
        headers: { ...(await orchestratorApi.getAuthHeaders()), 'Content-Type': 'application/json' },
        // datetime-local values are local time — pass them as-is so JS Date() parses as local time → correct ISO
        body: JSON.stringify({
          entityId: selectedEntityId,
          startDate: new Date(classifyStartDate).toISOString(),
          endDate: new Date(classifyEndDate).toISOString(),
        }),
      });
      const data = await resp.json();
      setClassifyResults(data.results || []);
      if (data.errors?.length) setClassifyRunErrors(data.errors);
    } catch (err) {
      console.error('Lead classification failed:', err);
      setClassifyRunErrors([String(err)]);
    } finally {
      setClassifyLoading(false);
    }
  };

  const handleSaveToCrm = async () => {
    setSavingToCrm(true);
    setClassifySaveStatus(null);
    try {
      const payload = classifyResults
        .filter(r => r.zoho_lead_id)
        .map(r => ({
          classificationId: r.id,
          chatId: r.chat_id,
          classification: classifyOverrides[r.id] ?? r.classification,
          zohoLeadId: r.zoho_lead_id!,
        }));

      const resp = await fetch('/api/admin/lead-classify/save-to-crm', {
        method: 'POST',
        headers: { ...(await orchestratorApi.getAuthHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ results: payload }),
      });
      const data = await resp.json();
      setClassifySaveStatus({ saved: data.saved || 0, failed: data.failed || 0, errors: data.errors || [] });

      // Mark saved rows in local state
      if (data.saved > 0) {
        setClassifyResults(prev => prev.map(r => {
          if (payload.find(p => p.classificationId === r.id)) {
            return { ...r, saved_to_crm: true };
          }
          return r;
        }));
      }
    } catch (err) {
      console.error('Save to CRM failed:', err);
    } finally {
      setSavingToCrm(false);
    }
  };

  // Track current sync job ID for WebSocket event filtering
  const currentSyncJobId = useRef<string | null>(null);

  // Stable handler ref to prevent duplicate event listeners
  const syncJobUpdateHandlerRef = useRef<((event: CustomEvent) => void) | undefined>();

  // Save selected entity ID to localStorage whenever it changes
  useEffect(() => {
    if (selectedEntityId) {
      localStorage.setItem('selectedEntityId', selectedEntityId);
    }
  }, [selectedEntityId]);

  // Sync with prop value (but only if prop has a valid value)
  useEffect(() => {
    if (propSelectedEntityId && propSelectedEntityId !== selectedEntityId) {
      setSelectedEntityId(propSelectedEntityId);
    }
  }, [propSelectedEntityId]);

  // NOTE: Removed circular dependency - ChatBoard receives selectedEntityId from App parent
  // No need to notify parent when entity changes since parent already controls the state

  // Load entities and restore selection
  useEffect(() => {
    const loadEntities = async () => {
      try {
        const authHeaders = await orchestratorApi.getAuthHeaders();
        const response = await fetch('/api/entities', { headers: authHeaders });
        const data = await response.json();

        if (data.ok && data.entities) {
          const formattedEntities: Entity[] = data.entities.map((entity: any) => ({
            id: entity.phone,
            name: entity.name,
            phone: entity.phone,
            status: entity.status === 'connected' ? 'connected' : 'disconnected',
            timezone: entity.timezone,
            tenantId: entity.tenant_id
          }));
          setEntities(formattedEntities);

          // If we have a saved entity ID and it exists in the loaded entities, use it
          const savedEntityId = localStorage.getItem('selectedEntityId');
          if (savedEntityId && formattedEntities.some(e => e.id === savedEntityId)) {
            setSelectedEntityId(savedEntityId);
          }
          // Otherwise, if no entity is selected and we have entities, select the first connected one
          else if (!selectedEntityId && formattedEntities.length > 0) {
            const connectedEntity = formattedEntities.find(e => e.status === 'connected');
            if (connectedEntity) {
              setSelectedEntityId(connectedEntity.id);
            } else {
              setSelectedEntityId(formattedEntities[0].id);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load entities:', error);
      }
    };

    loadEntities();
  }, []);

  // Notify App component when ChatBoard initializes with an entity from localStorage
  useEffect(() => {
    console.log('[ChatBoard] Entity initialization useEffect triggered:', {
      selectedEntityId,
      propSelectedEntityId,
      hasOnSelectedEntityChange: !!onSelectedEntityChange,
      shouldNotify: !selectedEntityId ? false : (!propSelectedEntityId || propSelectedEntityId !== selectedEntityId)
    });

    if (!selectedEntityId) {
      console.log('[ChatBoard] No entity to initialize, skipping notification');
      return;
    }

    console.log(`[ChatBoard] Entity initialized to ${selectedEntityId}, checking if App needs notification`);

    // Only notify App if App doesn't already have this entity (prevents unnecessary calls)
    if (onSelectedEntityChange && (!propSelectedEntityId || propSelectedEntityId !== selectedEntityId)) {
      console.log('[ChatBoard] Notifying App of entity initialization:', selectedEntityId);
      onSelectedEntityChange(selectedEntityId);
    } else {
      console.log('[ChatBoard] Not notifying App - already has entity or no callback');
    }
  }, [selectedEntityId, propSelectedEntityId, onSelectedEntityChange]); // Run when these are available

  // Poll WhatsApp status to update entity status in dropdown
  useEffect(() => {
    if (!entities.length || !selectedEntityId) return;

    const updateEntityStatus = async () => {
      try {
        const statusResponse = await orchestratorApi.getWhatsAppStatus(selectedEntityId);
        const normalizedStatus = statusResponse.status.toLowerCase();
        const connected = statusResponse.connected === true || normalizedStatus === 'connected';

        setWorkerStatus(statusResponse.worker_reachable === false ? 'offline' : 'online');
        setWhatsAppSessionStatus(
          connected
            ? 'connected'
            : statusResponse.qr_required
              ? 'qr_required'
              : ['connecting', 'authenticating', 'initializing'].includes(normalizedStatus)
                ? 'reconnecting'
                : 'disconnected'
        );

        // Update entity status in entities array for dropdown consistency
        setEntities(prev => prev.map(e =>
          e.id === selectedEntityId
            ? { ...e, status: connected ? 'connected' : 'disconnected' }
            : e
        ));
      } catch (error) {
        setWorkerStatus('offline');
        setWhatsAppSessionStatus('disconnected');
        setEntities(prev => prev.map(e =>
          e.id === selectedEntityId ? { ...e, status: 'disconnected' } : e
        ));
      }
    };

    // Initial fetch
    updateEntityStatus();

    // Poll every 5 seconds
    const statusInterval = setInterval(updateEntityStatus, 5000);
    return () => clearInterval(statusInterval);
  }, [entities.length, selectedEntityId]);

  // Note: ChatBoard now relies on App's chat state instead of loading chats independently
  // This prevents dual API calls and inconsistent chat lists
  useEffect(() => {
    if (!selectedEntityId) return;

    console.log(`[ChatBoard] Entity changed to ${selectedEntityId}, using App's chat state`);
  }, [selectedEntityId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSyncDropdown) {
        const target = event.target as Element;
        if (!target.closest('.sync-dropdown-container')) {
          setShowSyncDropdown(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showSyncDropdown]);

  // Listen for WebSocket sync job updates (for progress and error events only, not sync_complete)
  useEffect(() => {
    // Only register the listener once
    if (!syncJobUpdateHandlerRef.current) {
      console.log('[ChatBoard] Registering sync job update event listener (for progress/error only)');

      syncJobUpdateHandlerRef.current = (event: CustomEvent) => {
        const wsEvent = event.detail;

        // Only process events for the current sync job
        if (!currentSyncJobId.current || !wsEvent.data?.jobId) {
          return;
        }

        const eventJobId = wsEvent.data.jobId?.toString();
        if (eventJobId !== currentSyncJobId.current.toString()) {
          return;
        }

        console.log('[ChatBoard] Received sync job update:', wsEvent);

        switch (wsEvent.type) {
          case 'sync_progress':
            if (wsEvent.data) {
              const { totalChats, chatsProcessed, messagesProcessed } = wsEvent.data;
              setSyncProgress({
                message: `Syncing ${chatsProcessed || 0}/${totalChats || 0} chats...` +
                         (messagesProcessed ? ` (${messagesProcessed} messages)` : '')
              });
            }
            break;

          case 'chats_batch_synced':
            if (wsEvent.data) {
              const { batchCount, totalChats } = wsEvent.data;
              setSyncProgress({
                message: `Synced batch: ${batchCount}/${totalChats} chats...`
              });
            }
            break;

          case 'sync_error':
            console.error('[ChatBoard] Sync failed:', wsEvent.data?.error);
            setIsSyncing(false);
            setSyncProgress({
              message: `Sync failed: ${wsEvent.data?.error || 'Unknown error'}`,
              error: true
            });
            currentSyncJobId.current = null;
            // Clear error message after 5 seconds
            setTimeout(() => setSyncProgress(null), 5000);
            break;

          case 'sync_complete':
            console.log('[ChatBoard] Sync completed successfully');
            setIsSyncing(false);
            setSyncProgress({
              message: 'Sync completed successfully!'
            });
            currentSyncJobId.current = null;
            // Clear success message after 3 seconds
            setTimeout(() => setSyncProgress(null), 3000);
            break;
        }
      };

      window.addEventListener('sync-job-update', syncJobUpdateHandlerRef.current as EventListener);
    }

    return () => {
      // Cleanup is handled separately to prevent multiple removals
    };
  }, []);

  const handleEntitySelect = useCallback((entityId: string) => {
    console.log('[ChatBoard] Entity changed from', selectedEntityId, 'to', entityId, '- clearing selected chat and notifying App');
    setSelectedEntityId(entityId);
    setSelectedChat(null);

    // Notify App component to load chats for selected entity (one-way communication)
    if (onSelectedEntityChange) {
      onSelectedEntityChange(entityId);
    }
  }, [selectedEntityId, onSelectedEntityChange]);

  const handleSyncRecent = useCallback(async () => {
    await performSync(100, 10, 'Sync Recent');
  }, [selectedEntityId]);

  const handleSyncMax = useCallback(async () => {
    await performSync(0, 0, 'Sync Max');
  }, [selectedEntityId]);

  const performSync = useCallback(async (limit: number, messagesPerChat: number, syncType: string) => {
    // Enhanced validation to ensure entityId is properly set
    if (!selectedEntityId) {
      console.error('[ChatBoard] Sync failed: No entity selected');
      setSyncProgress({
        message: 'No entity selected. Please select an entity first.',
        error: true
      });
      setTimeout(() => setSyncProgress(null), 5000);
      return;
    }

    console.log(`[ChatBoard] Starting ${syncType} sync for entity: ${selectedEntityId} (limit: ${limit}, messagesPerChat: ${messagesPerChat})`);
    setShowSyncDropdown(false);
    setIsSyncing(true);
    setSyncProgress({ message: `Starting ${syncType}...` });

    try {
      const result = await orchestratorApi.syncWhatsAppChats(limit, messagesPerChat, selectedEntityId);

      if (result.success && result.jobId) {
        // Store the job ID to filter WebSocket events
        currentSyncJobId.current = result.jobId.toString();
        setSyncProgress({ message: `${syncType} in progress...` });
      } else {
        // API returned an error
        setIsSyncing(false);

        // Check if it's a worker authentication error
        if (result.error && result.error.includes('not authenticated') && result.qrRefreshUrl) {
          setSyncProgress({
            message: result.error,
            error: true,
            qrRefreshUrl: result.qrRefreshUrl
          });
        } else {
          setSyncProgress({
            message: result.error || `Failed to start ${syncType}`,
            error: true
          });
        }

        currentSyncJobId.current = null;
        // Clear error message after 8 seconds (longer for QR code message)
        setTimeout(() => setSyncProgress(null), 8000);
      }
    } catch (error) {
      console.error(`[ChatBoard] Failed to ${syncType.toLowerCase()}:`, error);
      setIsSyncing(false);
      setSyncProgress({
        message: `Failed to start ${syncType}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        error: true
      });
      currentSyncJobId.current = null;
      // Clear error message after 5 seconds
      setTimeout(() => setSyncProgress(null), 5000);
    }
  }, [selectedEntityId]);

  // @ts-ignore - unused function
  const handleSyncAll = useCallback(async () => {
    if (!selectedEntityId) return;

    setIsSyncing(true);
    setSyncProgress({ message: 'Starting full sync...' });

    try {
      await orchestratorApi.syncWhatsAppChats(0, 0, selectedEntityId);
      setSyncProgress({ message: 'Full sync initiated...' });
    } catch (error) {
      console.error('Failed to sync all chats:', error);
      setIsSyncing(false);
      setSyncProgress(null);
    }
  }, [selectedEntityId]);

  const filteredChats = useMemo(() => {
    if (!selectedEntityId) return [];

    let filtered = chats;

    console.log('[ChatBoard] Using chats from App (should already be filtered by entity):', {
      selectedEntityId,
      totalChats: chats.length,
      sampleChats: filtered.slice(0, 3).map(c => ({ id: c.chat_id, name: c.name }))
    });

    if (chatSearchTerm.trim()) {
      const searchLower = chatSearchTerm.toLowerCase();
      filtered = filtered.filter(chat =>
        chat.chat_id.toLowerCase().includes(searchLower) ||
        chat.phone_number.toLowerCase().includes(searchLower) ||
        (chat.last_message && chat.last_message.toLowerCase().includes(searchLower))
      );
    }

    if (productFilter) {
      filtered = filtered.filter(chat => chat.zoho_product === productFilter);
    }

    if (leadStatusFilter) {
      filtered = filtered.filter(chat => chat.zoho_lead_status === leadStatusFilter);
    }

    if (leadDateFrom) {
      const from = new Date(leadDateFrom);
      filtered = filtered.filter(chat =>
        chat.zoho_lead_created_at && new Date(chat.zoho_lead_created_at) >= from
      );
    }

    if (leadDateTo) {
      const to = new Date(leadDateTo);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter(chat =>
        chat.zoho_lead_created_at && new Date(chat.zoho_lead_created_at) <= to
      );
    }

    return filtered;
  }, [chats, selectedEntityId, chatSearchTerm, productFilter, leadStatusFilter, leadDateFrom, leadDateTo]);

  // Fetch available Zoho products for filter dropdown
  useEffect(() => {
    orchestratorApi.getZohoProducts().then(setAvailableProducts).catch(() => {});
  }, []);

  const availableLeadStatuses = useMemo(() => {
    return [...new Set(
      chats
        .map(chat => chat.zoho_lead_status?.trim())
        .filter((status): status is string => Boolean(status))
    )].sort((a, b) => a.localeCompare(b));
  }, [chats]);

  // Load lead info for all visible chats when chat list changes
  useEffect(() => {
    filteredChats.forEach(chat => {
      if (getPreferredChatPhone(chat) && !chat.is_group && !chat.zoho_lead_status) {
        fetchZohoLeadInfo(chat);
      }
    });
  }, [filteredChats.map(c => `${c.chat_id}:${c.derived_phone_number || ''}:${c.derived_client_name || ''}`).join(','), selectedEntityId]); // Only re-run when identity changes

  useEffect(() => {
    const loadBroadcastHistory = async () => {
      if (!selectedChat?.chat_id || selectedChat.is_group) {
        setBroadcastHistory([]);
        return;
      }

      setLoadingBroadcastHistory(true);
      try {
        const response = await fetch(`/api/chats/${encodeURIComponent(selectedChat.chat_id)}/broadcast-history?limit=20`, {
          headers: await orchestratorApi.getAuthHeaders()
        });
        const data = await response.json();
        setBroadcastHistory(data.ok ? (data.history || []) : []);
      } catch (error) {
        console.error('Failed to load broadcast history:', error);
        setBroadcastHistory([]);
      } finally {
        setLoadingBroadcastHistory(false);
      }
    };

    loadBroadcastHistory();
  }, [selectedChat?.chat_id, selectedChat?.is_group]);

  // Render Zoho lead status badge
  const renderZohoLeadBadge = (chat: Chat, small?: boolean) => {
    const leadInfo = zohoLeadInfoCache.get(chat.chat_id);
    const leadStatus = chat.zoho_lead_status || leadInfo?.lead_status;

    const resolveResponseMode = (status?: string | null): 'normal' | 'light' | 'none' => {
      if (status === 'None' || status === 'Attempted to Contact') {
        return 'light';
      }
      if (status === 'Junk Lead' || status === 'Lost Lead') {
        return 'none';
      }
      return 'normal';
    };

    if (!chat.zoho_lead_status && !leadInfo) {
      // Haven't fetched yet, will load soon
      return null;
    }

    if (!chat.zoho_lead_status && leadInfo?.loading) {
      return (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: small ? '2px 6px' : '4px 8px',
          background: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: small ? '3px' : '4px',
          fontSize: small ? '10px' : '11px',
          color: '#6c757d'
        }}>
          <span style={{
            width: small ? '8px' : '10px',
            height: small ? '8px' : '10px',
            border: `2px solid #e0e0e0`,
            borderTop: `2px solid #6c757d`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            display: 'inline-block'
          }}></span>
        </div>
      );
    }

    if (!leadStatus) {
      return null;
    }

    if (leadStatus === 'Closed Deal') {
      const closedDealColors = {
        bg: '#1b5e20',
        border: '#154a19',
        text: '#ffffff',
        dot: '#ffffff'
      };

      return (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: small ? '4px' : '6px',
          padding: small ? '2px 6px' : '4px 10px',
          background: closedDealColors.bg,
          border: `1px solid ${closedDealColors.border}`,
          borderRadius: small ? '3px' : '4px',
          fontSize: small ? '10px' : '12px',
          fontWeight: '500',
          color: closedDealColors.text,
          marginLeft: small ? '4px' : '0'
        }} title={`Lead Status: ${leadStatus}`}>
          <span style={{
            display: 'inline-block',
            width: small ? '6px' : '7px',
            height: small ? '6px' : '7px',
            borderRadius: '50%',
            background: closedDealColors.dot
          }}></span>
          <span style={{
            maxWidth: small ? '80px' : '120px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>{leadStatus}</span>
        </div>
      );
    }

    const modeColors = {
      normal: { bg: '#d4edda', border: '#c3e6cb', text: '#155724', dot: '#28a745' },
      light: { bg: '#fff3cd', border: '#ffeaa7', text: '#856404', dot: '#ffc107' },
      none: { bg: '#f8d7da', border: '#f5c6cb', text: '#721c24', dot: '#dc3545' }
    };

    const colors = modeColors[leadInfo?.response_mode || resolveResponseMode(leadStatus)];

    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: small ? '4px' : '6px',
        padding: small ? '2px 6px' : '4px 10px',
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: small ? '3px' : '4px',
        fontSize: small ? '10px' : '12px',
        fontWeight: '500',
        color: colors.text,
        marginLeft: small ? '4px' : '0'
      }} title={`Lead Status: ${leadStatus}`}>
        <span style={{
          display: 'inline-block',
          width: small ? '6px' : '7px',
          height: small ? '6px' : '7px',
          borderRadius: '50%',
          background: colors.dot
        }}></span>
        <span style={{
          maxWidth: small ? '80px' : '120px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>{leadStatus}</span>
      </div>
    );
  };

  // Auto-select top chat when chats are loaded and no chat is selected
  useEffect(() => {
    console.log('[ChatBoard] Auto-select effect:', {
      filteredChatsCount: filteredChats.length,
      selectedChatId: selectedChat?.chat_id || null,
      selectedEntityId
    });

    // Clear selected chat when entity changes
    if (selectedEntityId && selectedChat) {
      // Check if selected chat belongs to current entity (by checking if chat is in filtered list)
      const chatBelongsToCurrentEntity = filteredChats.some(chat => chat.chat_id === selectedChat.chat_id);
      if (!chatBelongsToCurrentEntity) {
        console.log('[ChatBoard] Clearing selected chat - belongs to different entity:', selectedChat.chat_id);
        setSelectedChat(null);
        return;
      }
    }

    if (filteredChats.length > 0 && !selectedChat) {
      // Find the first valid chat (skip placeholder or invalid chat IDs)
      const firstValidChat = filteredChats.find(chat =>
        chat.chat_id &&
        chat.chat_id !== '0@c.us' &&
        chat.chat_id !== '0@g.us' &&
        !chat.chat_id.startsWith('0@') &&
        chat.chat_id.includes('@') &&
        chat.chat_id.length > 5
      );

      if (firstValidChat) {
        console.log('[ChatBoard] Auto-selecting first valid chat:', firstValidChat.chat_id);
        setSelectedChat(firstValidChat);
      } else {
        console.log('[ChatBoard] No valid chats found to auto-select');
      }
    }
  }, [filteredChats, selectedChat, selectedEntityId]);

  // Fetch AI pause status when chat is selected and poll every 30 seconds
  useEffect(() => {
    if (!selectedChat?.chat_id) {
      setAiPauseStatus(null);
      return;
    }

    const fetchPauseStatus = async () => {
      try {
        const status = await orchestratorApi.getChatPauseStatus(selectedChat.chat_id);
        setAiPauseStatus(status);
      } catch (error) {
        console.error('Failed to fetch AI pause status:', error);
      }
    };

    // Fetch immediately
    fetchPauseStatus();

    // Poll every 30 seconds
    const interval = setInterval(fetchPauseStatus, 30000);

    return () => clearInterval(interval);
  }, [selectedChat?.chat_id]);

  // Reset user selection flag after it's been used (allows scroll-to-bottom to happen once)
  useEffect(() => {
    if (userExplicitlySelectedChat) {
      const timer = setTimeout(() => {
        setUserExplicitlySelectedChat(false);
      }, 300); // Reset after scroll completes
      return () => clearTimeout(timer);
    }
  }, [userExplicitlySelectedChat]);

  // @ts-ignore - unused variable
  const selectedEntity = useMemo(() => {
    return entities.find(e => e.id === selectedEntityId);
  }, [entities, selectedEntityId]);

  const connectionIndicator = useMemo(() => {
    if (workerStatus === 'offline' || wsStatus === 'disconnected') {
      return {
        cssStatus: 'disconnected',
        label: '🔴 Offline',
        tooltip: 'The application cannot currently reach the assigned WhatsApp worker.'
      };
    }

    if (workerStatus === 'checking' || whatsappSessionStatus === 'checking' || wsStatus === 'connecting') {
      return {
        cssStatus: 'connecting',
        label: '🟡 Checking connection…',
        tooltip: 'Checking the assigned worker and WhatsApp browser session.'
      };
    }

    if (whatsappSessionStatus === 'connected') {
      return {
        cssStatus: 'connected',
        label: '🟢 Connected',
        tooltip: 'The assigned worker and WhatsApp browser session are connected.'
      };
    }

    if (whatsappSessionStatus === 'reconnecting' || whatsappSessionStatus === 'qr_required') {
      return {
        cssStatus: 'connecting',
        label: whatsappSessionStatus === 'qr_required'
          ? '🟡 WhatsApp not live'
          : '🟡 Reconnecting',
        tooltip: whatsappSessionStatus === 'qr_required'
          ? 'Worker: online. WhatsApp browser session: authentication pending. Synced chat history remains available, but live messaging is not currently verified.'
          : 'Worker: online. WhatsApp browser session: reconnecting.'
      };
    }

    return {
      cssStatus: 'disconnected',
      label: '🔴 Disconnected',
      tooltip: 'The worker is online, but the WhatsApp browser session is not connected.'
    };
  }, [workerStatus, whatsappSessionStatus, wsStatus]);

  if (!selectedEntityId) {
    return (
      <div className="chat-board-empty">
        <div className="empty-icon">📱</div>
        <h2>No Entity Selected</h2>
        <p>Please select an entity to view and manage chats.</p>
      </div>
    );
  }

  return (
    <>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
    <div className="chat-board-layout-new">
      {/* Top Bar with Entity Dropdown and Controls */}
      <div className="chat-board-header">
        <div className="header-left">
          <div className="entity-dropdown">
            <select
              value={selectedEntityId}
              onChange={(e) => handleEntitySelect(e.target.value)}
              className="entity-select"
            >
              {entities.map(entity => (
                <option key={entity.id} value={entity.id}>
                  {entity.name} ({entity.phone})
                </option>
              ))}
            </select>
            <button
              type="button"
              className={`system-status-inline ${connectionIndicator.cssStatus}`}
              aria-label={`${connectionIndicator.label}. ${connectionIndicator.tooltip}`}
            >
              {connectionIndicator.label} <span aria-hidden="true">ⓘ</span>
              <span className="connection-status-tooltip" role="tooltip">
                {connectionIndicator.tooltip}
                <small>Tap or hover this status to view details.</small>
              </span>
            </button>
          </div>
        </div>

        <div className="header-right">
          <button
            onClick={() => setShowSettings(true)}
            className="control-btn"
            title="Configure model preferences and API keys"
          >
            ⚙️ Settings
          </button>

          <button
            onClick={() => setShowMemoryPanel(!showMemoryPanel)}
            className={`control-btn ${showMemoryPanel ? 'active' : ''}`}
            title="View and manage client memory"
          >
            🧠 Memory
          </button>

          <div className="sync-dropdown-container" style={{ position: 'relative', display: 'inline-block' }}>
            <button
              onClick={() => setShowSyncDropdown(!showSyncDropdown)}
              disabled={isSyncing}
              className="control-btn"
              title="Fetch chats from WhatsApp Web and save to database"
            >
              {isSyncing ? "⏳" : "📥"} Sync ▼
            </button>

            {showSyncDropdown && (
              <div className="sync-dropdown-menu" style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: 1000,
                minWidth: '180px'
              }}>
                <button
                  onClick={() => handleSyncRecent()}
                  disabled={isSyncing}
                  className="sync-dropdown-item"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    cursor: isSyncing ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                  title="Fetch recent chats (limit 100, 10 messages each) - Faster"
                >
                  📥 Sync Recent
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    Fast: 100 chats, 10 msg each
                  </div>
                </button>

                <button
                  onClick={() => handleSyncMax()}
                  disabled={isSyncing}
                  className="sync-dropdown-item"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    cursor: isSyncing ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    borderTop: '1px solid #eee'
                  }}
                  title="Fetch ALL chats with ALL messages - Slower but comprehensive"
                >
                  📊 Sync Max
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    Complete: All chats, 100k messages/chat
                  </div>
                </button>
              </div>
            )}
          </div>


          <button
            onClick={() => selectedEntityId && onRefreshChatsWithEntityId?.(selectedEntityId)}
            disabled={loadingMore || !selectedEntityId}
            className="control-btn"
          >
            🔄 Refresh
          </button>

          <button
            onClick={() => {
              setWorkDayTime();
              setShowSummarySettings(true);
            }}
            className="control-btn"
            title="Summarize conversations for a date range"
          >
            📊 Summarize
          </button>

          <button
            onClick={async () => {
              setShowUnansweredConfirmModal(true);
              if (selectedEntityId) {
                try {
                  const res = await fetch(`/api/unanswered/entity-assignments/${selectedEntityId}/llm-model`, {
                    headers: await orchestratorApi.getAuthHeaders()
                  });
                  const data = await res.json();
                  if (data.ok) setUnansweredLlmModel(data.llmModel);
                } catch {}
              }
            }}
            disabled={loadingUnanswered}
            className="control-btn"
            title="Batch process chats to find and answer unanswered client messages"
          >
            {loadingUnanswered ? '⏳' : '💬'} Unanswered
          </button>

          <button
            onClick={() => setShowCreateGroupModal(true)}
            className="control-btn"
            title="Create a new WhatsApp group"
            disabled={!selectedEntityId}
          >
            👥 Create Group
          </button>

          <button
            onClick={handleClassifyLeads}
            disabled={classifyLoading || !selectedEntityId}
            className="control-btn"
            title="Classify uncontacted leads using AI"
          >
            {classifyLoading ? '⏳' : '🏷️'} Classify
          </button>
        </div>
      </div>

      {/* Sync Progress */}
      {syncProgress && (
        <div className={`sync-progress-bar ${syncProgress.error ? 'error' : ''}`}>
          <span>{syncProgress.message}</span>
          {syncProgress.qrRefreshUrl && (
            <>
              <br />
              <a
                href={syncProgress.qrRefreshUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#c33',
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  display: 'inline-block',
                  marginTop: '4px'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                📱 Click here to scan QR code and authenticate WhatsApp
              </a>
            </>
          )}
        </div>
      )}

      {/* Main Content Area */}
      <div className="chat-board-content">
        {/* Left Panel - Chat List */}
        <div className="chat-list-panel">
          <div className="chat-list-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <h3 style={{ margin: 0 }}>Chats ({filteredChats.length})</h3>
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <button
                  onClick={() => onLoadChatsWithLimit ? onLoadChatsWithLimit(selectedLoadLimit) : onLoadAllChats?.()}
                  disabled={loadingMore}
                  title={`Load ${selectedLoadLimit} chats from database`}
                  style={{
                    padding: '2px 6px',
                    fontSize: '11px',
                    background: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRight: 'none',
                    borderRadius: '4px 0 0 4px',
                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                    color: '#374151',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {loadingMore ? '⏳' : '⬆'} {selectedLoadLimit}
                </button>
                <button
                  onClick={() => setShowLoadDropdown(!showLoadDropdown)}
                  disabled={loadingMore}
                  title="Select number of chats to load"
                  style={{
                    padding: '2px 5px',
                    fontSize: '10px',
                    background: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '0 4px 4px 0',
                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                    color: '#374151'
                  }}
                >
                  ▼
                </button>
                {showLoadDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '110px'
                  }}>
                    {[100, 200, 300, 400, 500].map((limit) => (
                      <button
                        key={limit}
                        onClick={() => { setSelectedLoadLimit(limit); setShowLoadDropdown(false); }}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '6px 12px',
                          textAlign: 'left',
                          border: 'none',
                          background: selectedLoadLimit === limit ? '#f0f0f0' : 'none',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: selectedLoadLimit === limit ? 'bold' : 'normal'
                        }}
                      >
                        {limit} chats
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="search-box">
              <input
                type="text"
                placeholder="Search chats..."
                value={chatSearchTerm}
                onChange={(e) => setChatSearchTerm(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>
            {/* Zoho Product + Lead Status + Lead Date Filters */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '4px 8px 4px 8px' }}>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <select
                  value={productFilter}
                  onChange={(e) => setProductFilter(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '4px 6px',
                    fontSize: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    background: productFilter ? '#e8f0fe' : 'white',
                    color: productFilter ? '#1a73e8' : '#6b7280',
                  }}
                >
                  <option value="">All Products</option>
                  {availableProducts.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                {(productFilter || leadStatusFilter || leadDateFrom || leadDateTo) && (
                  <button
                    onClick={() => { setProductFilter(''); setLeadStatusFilter(''); setLeadDateFrom(''); setLeadDateTo(''); }}
                    title="Clear filters"
                    style={{
                      padding: '3px 7px',
                      fontSize: '11px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      background: '#fee2e2',
                      color: '#dc2626',
                      cursor: 'pointer',
                    }}
                  >✕</button>
                )}
              </div>
              {availableLeadStatuses.length > 0 && (
                <select
                  value={leadStatusFilter}
                  onChange={(e) => setLeadStatusFilter(e.target.value)}
                  style={{
                    padding: '4px 6px',
                    fontSize: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    background: leadStatusFilter ? '#e8f0fe' : 'white',
                    color: leadStatusFilter ? '#1a73e8' : '#6b7280',
                  }}
                >
                  <option value="">All Lead Statuses</option>
                  {availableLeadStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              )}
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input
                  type="date"
                  value={leadDateFrom}
                  onChange={(e) => setLeadDateFrom(e.target.value)}
                  title="Lead created from"
                  style={{
                    flex: 1,
                    padding: '3px 6px',
                    fontSize: '11px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    color: leadDateFrom ? '#1a73e8' : '#9ca3af',
                  }}
                />
                <span style={{ fontSize: '10px', color: '#9ca3af', flexShrink: 0 }}>–</span>
                <input
                  type="date"
                  value={leadDateTo}
                  onChange={(e) => setLeadDateTo(e.target.value)}
                  title="Lead created to"
                  style={{
                    flex: 1,
                    padding: '3px 6px',
                    fontSize: '11px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    color: leadDateTo ? '#1a73e8' : '#9ca3af',
                  }}
                />
              </div>
            </div>
          </div>
          <div className="chats-list">
            {filteredChats.length === 0 ? (
              <div className="empty-state">No chats found</div>
            ) : (
              filteredChats.map((chat) => (
                <div
                  key={chat.chat_id}
                  className={`chat-item ${selectedChat && selectedChat.chat_id === chat.chat_id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedChat(chat);
                    // Signal that user explicitly selected this chat from left panel
                    setUserExplicitlySelectedChat(true);
                  }}
                >
                  <div className="chat-item-header">
                    <span className="chat-id">
                      {/* Display group indicator and name, or contact name, or formatted phone */}
                      {chat.is_group ? (
                        <>
                          👥 {getPreferredChatName(chat)}
                          {chat.participants && ` (${chat.participants})`}
                        </>
                      ) : (
                        <>{chat.lead_display_name || getPreferredChatName(chat)}</>
                      )}
                    </span>
                    {/* QUALIFIED STATUS BADGES TEMPORARILY DISABLED */}
                    {/* {chat.status === 'qualified' && (
                      <span className="status-badge status-qualified">
                        {chat.status}
                      </span>
                    )} */}
                  </div>
                  <div className="chat-item-info">
                    <div className="chat-phone" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexWrap: 'wrap'
                    }}>
                      {/* Show phone number only if it's not a group */}
                      <span>
                        {!chat.is_group && formatPhoneNumber(normalizePhoneNumber(getPreferredChatPhone(chat)))}
                        {chat.is_group && `Group chat`}
                      </span>

                      {/* Zoho Product Badge */}
                      {chat.zoho_product && (
                        <span style={{
                          display: 'inline-block',
                          padding: '1px 6px',
                          background: '#0066cc',
                          color: 'white',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: '600',
                          letterSpacing: '0.3px',
                        }}>
                          {chat.zoho_product}
                        </span>
                      )}

                      {chat.order_value_cents != null && (
                        <span className="business-tag order-value">S${(chat.order_value_cents / 100).toFixed(2)}</span>
                      )}
                      {chat.document_type && (
                        <span className="business-tag document-type">{chat.document_type}</span>
                      )}
                      {chat.use_case && (
                        <span className="business-tag use-case">{chat.use_case}</span>
                      )}
                      {chat.payment_status && chat.payment_status !== 'unknown' && (
                        <span className={`business-tag payment-${chat.payment_status}`}>
                          {chat.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                        </span>
                      )}

                      {/* Zoho Lead Status Badge */}
                      {!chat.is_group && renderZohoLeadBadge(chat, true)}

                      {!!chat.broadcast_count && chat.broadcast_count > 0 && (
                        <span style={{
                          display: 'inline-block',
                          padding: '1px 6px',
                          background: '#fff3cd',
                          color: '#8a5a00',
                          border: '1px solid #f6d88a',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: '600',
                        }}>
                          Broadcast x{chat.broadcast_count}
                        </span>
                      )}
                    </div>
                    <div className="chat-last-message">
                      {chat.last_message || 'No messages'}
                    </div>
                    <div className="chat-timestamp" style={{
                      fontSize: '11px',
                      color: '#666',
                      marginTop: '2px',
                      fontStyle: 'italic'
                    }}>
                      {chat.last_message_at ? formatDateTime(chat.last_message_at, selectedEntity?.timezone) :
                       chat.created_at ? formatDateTime(chat.created_at, selectedEntity?.timezone) : ''}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Vertical Divider */}
        <div className="vertical-divider"></div>

        {/* Right Panel - Chat Messages and Reply */}
        <div className="chat-messages-panel">
          {selectedChat ? (
            <>
              {/* Message Search Bar */}
              <div className="messages-search-header">
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={messageSearchTerm}
                  onChange={(e) => setMessageSearchTerm(e.target.value)}
                  className="search-input"
                />
                <span className="search-icon">🔍</span>
              </div>

              {/* AI Pause Countdown Timer */}
              {aiPauseStatus?.ai_paused && aiPauseStatus.minutes_remaining > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  margin: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  <span style={{ fontSize: '20px' }}>⏸️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                      AI Agent Paused
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>
                      Human salesperson intervened - AI will resume in {aiPauseStatus.minutes_remaining} minute{aiPauseStatus.minutes_remaining !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontWeight: '700',
                    fontSize: '16px'
                  }}>
                    {aiPauseStatus.minutes_remaining}m
                  </div>
                </div>
              )}

              {/* Chat Messages */}
              <div className="chat-messages-container">
                {selectedChat ? (
                  <ChatMessages
                    chat={selectedChat}
                    entityId={selectedEntityId}
                    onChatIdentityDetected={handleDetectedChatIdentity}
                    userSelectedFromPanel={userExplicitlySelectedChat}
                    zohoLeadBadge={renderZohoLeadBadge(selectedChat, false)}
                    broadcastHistory={broadcastHistory}
                    loadingBroadcastHistory={loadingBroadcastHistory}
                  />
                ) : (
                  <div className="chat-messages-empty">
                    <div className="empty-icon">💬</div>
                    <h3>Select a Chat</h3>
                    <p>Choose a chat from the left to view messages.</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="chat-board-empty-state">
              <div className="empty-icon">💬</div>
              <h3>Select a Chat</h3>
              <p>Choose a chat from the left to view messages.</p>
            </div>
          )}
        </div>

        {/* Memory Review Panel (Side Panel) */}
        {showMemoryPanel && (
          <>
            {/* Vertical Divider */}
            <div className="vertical-divider"></div>

            {/* Memory Panel */}
            <div className="memory-review-panel" style={{
              flex: '0 0 350px',
              display: 'flex',
              flexDirection: 'column',
              background: '#f8f9fa',
              borderLeft: '1px solid #e0e0e0'
            }}>
              <div style={{
                padding: '1rem',
                background: 'white',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>🧠 Client Memory</h3>
                <button
                  onClick={() => setShowMemoryPanel(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.2rem',
                    cursor: 'pointer',
                    padding: '0.25rem 0.5rem'
                  }}
                  title="Close Memory Panel"
                >
                  ✕
                </button>
              </div>

              {selectedChat ? (
                <MemoryReviewPanel
                  tenantId={selectedEntity?.tenantId || authTenantId || ''}
                  contactId={selectedChat.chat_id}
                  chatId={selectedChat.chat_id}
                  onMemoryUpdate={() => {
                    // Trigger chat refresh when memory is updated
                    selectedEntityId && onRefreshChatsWithEntityId?.(selectedEntityId);
                  }}
                  reviewedBy={user?.email || 'unknown'}
                />
              ) : (
                <div style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  <p>Select a chat to view client memory</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Model Settings Modal */}
      <ModelSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        tenantId={selectedEntity?.tenantId || authTenantId || ''}
        userId={undefined}
      />

      {/* Summary Settings Modal */}
      {showSummarySettings && (
        <div className="modal-overlay" onClick={() => setShowSummarySettings(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Summarize Conversations</h2>

            {/* Preset buttons */}
            <div className="preset-buttons">
              <button onClick={setWorkDayTime}>Work Day (9am-6pm today)</button>
              <button onClick={setAfterHoursTime}>After Hours (6pm yesterday - 9am today)</button>
            </div>

            {/* Date/time inputs */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                Start Time:
                <input
                  type="datetime-local"
                  value={summaryStartTime}
                  onChange={(e) => setSummaryStartTime(e.target.value)}
                  style={{ marginLeft: '0.5rem', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </label>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                End Time:
                <input
                  type="datetime-local"
                  value={summaryEndTime}
                  onChange={(e) => setSummaryEndTime(e.target.value)}
                  style={{ marginLeft: '0.5rem', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </label>
            </div>

            {summaryError && (
              <div style={{ color: 'red', marginBottom: '1rem' }}>{summaryError}</div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={fetchSummaries}
                disabled={loadingSummaries}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loadingSummaries ? 'not-allowed' : 'pointer',
                  opacity: loadingSummaries ? 0.6 : 1
                }}
              >
                {loadingSummaries ? 'Generating...' : 'Generate Summaries'}
              </button>
              <button
                onClick={() => setShowSummarySettings(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Results Modal */}
      {showSummaryModal && (
        <div className="modal-overlay" onClick={() => setShowSummaryModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <h2>Conversation Summaries</h2>
            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Timezone: {selectedEntity?.timezone || 'Asia/Singapore'}
            </p>
            <p>Date Range: {summaryStartTime && formatDateTimeWithTimezone(summaryStartTime, selectedEntity?.timezone)} - {summaryEndTime && formatDateTimeWithTimezone(summaryEndTime, selectedEntity?.timezone)}</p>
            <p>Total Chats: {summaries.length}</p>

            {/* Zoho push result banner */}
            {zohoPushResult && (
              <div style={{
                padding: '0.75rem 1rem',
                borderRadius: '6px',
                marginBottom: '1rem',
                background: zohoPushResult.failedCount > 0 ? '#fef3c7' : '#d1fae5',
                border: `1px solid ${zohoPushResult.failedCount > 0 ? '#f59e0b' : '#10b981'}`,
                fontSize: '0.9rem',
              }}>
                <strong>CRM Push Result:</strong>{' '}
                {zohoPushResult.successCount} pushed successfully
                {zohoPushResult.noLeadCount > 0 && `, ${zohoPushResult.noLeadCount} no lead found`}
                {zohoPushResult.failedCount > 0 && `, ${zohoPushResult.failedCount} failed`}
              </div>
            )}

            {summaries.length === 0 ? (
              <p>No conversations found for this period.</p>
            ) : (
              <div className="summary-list">
                {summaries.map((summary: any) => (
                  <div key={summary.chatId} className="summary-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <h3 style={{ margin: 0 }}>{summary.chatName || summary.phoneNumber}</h3>
                      {/* Per-summary Zoho push status badge */}
                      {summary.zohoPushStatus === 'success' && (
                        <span style={{
                          fontSize: '0.75rem',
                          background: '#d1fae5',
                          color: '#065f46',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          border: '1px solid #10b981',
                          whiteSpace: 'nowrap',
                          marginLeft: '0.5rem',
                        }}>
                          CRM updated {summary.zohoPushedAt ? formatDateTimeWithTimezone(summary.zohoPushedAt, selectedEntity?.timezone) : ''}
                        </span>
                      )}
                      {summary.zohoPushStatus === 'failed' && (
                        <span style={{
                          fontSize: '0.75rem',
                          background: '#fee2e2',
                          color: '#991b1b',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          border: '1px solid #ef4444',
                          whiteSpace: 'nowrap',
                          marginLeft: '0.5rem',
                        }}>
                          CRM push failed
                        </span>
                      )}
                    </div>
                    <p className="summary-text">{summary.summary}</p>

                    {summary.actionItems.unansweredQuestions.length > 0 && (
                      <div className="action-section">
                        <h4>❓ Unanswered Questions:</h4>
                        <ul>
                          {summary.actionItems.unansweredQuestions.map((q: string, i: number) => (
                            <li key={i}>{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {summary.actionItems.followUps.length > 0 && (
                      <div className="action-section">
                        <h4>✅ Follow-ups:</h4>
                        <ul>
                          {summary.actionItems.followUps.map((f: string, i: number) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {summary.actionItems.bookedMeetings.length > 0 && (
                      <div className="action-section">
                        <h4>📅 Booked Meetings:</h4>
                        <ul>
                          {summary.actionItems.bookedMeetings.map((m: string, i: number) => (
                            <li key={i}>{m}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <p className="summary-meta">
                      {summary.messageCount} messages | Last: {formatDateTimeWithTimezone(summary.lastMessageAt, selectedEntity?.timezone)}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              {summaries.length > 0 && (
                <button
                  onClick={handlePushToZoho}
                  disabled={pushingToZoho}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: pushingToZoho ? '#9ca3af' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: pushingToZoho ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                  }}
                >
                  {pushingToZoho ? 'Pushing to CRM...' : 'Push to CRM'}
                </button>
              )}
              <button
                onClick={() => {
                  setShowSummaryModal(false);
                  setZohoPushResult(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unanswered Messages Confirmation Modal */}
      {showUnansweredConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowUnansweredConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div>
                <h2 style={{ margin: 0 }}>💬 Unanswered Messages</h2>
                {unansweredLlmModel && (
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '2px', display: 'block' }}>
                    🤖 {unansweredLlmModel}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowUnansweredConfirmModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '0.75rem', background: '#fef3c7', borderRadius: '0.5rem', marginBottom: '1.5rem', border: '1px solid #f59e0b' }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#92400e' }}>
                <strong>Analyzing:</strong> {entities.find(e => e.id === selectedEntityId)?.name || selectedEntityId}
              </p>
            </div>

            <p style={{ marginBottom: '1.5rem', color: '#6b7280' }}>
              Analyze recent conversations to find unanswered client messages that need responses.
            </p>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                How many chats to analyze?
              </label>
              <input
                type="number"
                min="1"
                max="500"
                value={maxChatsInput}
                onChange={(e) => setMaxChatsInput(Math.min(500, Math.max(1, parseInt(e.target.value) || 100)))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '1rem'
                }}
              />
              <p style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                More chats = longer processing time (typically 10–20 seconds)
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Minimum message age (minutes)
              </label>
              <input
                type="number"
                min="1"
                max="10080"
                value={minutesThresholdInput}
                onChange={(e) => setMinutesThresholdInput(Math.min(10080, Math.max(1, parseInt(e.target.value) || 30)))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '1rem'
                }}
              />
              <p style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                Only flag chats where the last client message is older than this many minutes (e.g. 30 = half an hour, 1440 = 1 day).
                Messages newer than this are ignored — the client may still be typing. Only messages from the last 2 months are considered.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                {[15, 30, 60, 180, 1440].map((mins) => (
                  <button
                    key={mins}
                    onClick={() => setMinutesThresholdInput(mins)}
                    style={{
                      padding: '3px 10px',
                      fontSize: '0.8rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '1rem',
                      background: minutesThresholdInput === mins ? '#10b981' : '#f9fafb',
                      color: minutesThresholdInput === mins ? 'white' : '#374151',
                      cursor: 'pointer'
                    }}
                  >
                    {mins < 60 ? `${mins}m` : mins < 1440 ? `${mins / 60}h` : `${mins / 1440}d`}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowUnansweredConfirmModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => checkUnansweredMessages(maxChatsInput, minutesThresholdInput)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Analyze
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unanswered Messages Modal */}
      {showUnansweredModal && (
        <div className="modal-overlay" onClick={() => setShowUnansweredModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>💬 Unanswered Messages</h2>
              <button
                onClick={() => setShowUnansweredModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            {/* Progress bar — always visible while loading */}
            {(loadingUnanswered || unansweredProgress) && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  {loadingUnanswered && (
                    <span style={{
                      display: 'inline-block', width: '14px', height: '14px',
                      border: '2px solid #e5e7eb', borderTop: '2px solid #10b981',
                      borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0
                    }} />
                  )}
                  <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: '500' }}>
                    {unansweredProgress?.message || 'Initializing...'}
                  </span>
                </div>
                {unansweredProgress && (
                  <div style={{ width: '100%', height: '6px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(100, (unansweredProgress.current / unansweredProgress.total) * 100)}%`,
                      height: '100%', background: '#10b981', transition: 'width 0.4s ease', borderRadius: '4px'
                    }} />
                  </div>
                )}
                {unansweredProgress && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {unansweredProgress.current} / {unansweredProgress.total} chats checked
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '500' }}>
                      {unansweredChats.length} unanswered found
                    </span>
                  </div>
                )}
              </div>
            )}

            {!loadingUnanswered && unansweredChats.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p>✅ No unanswered messages found!</p>
                <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                  All messages have been responded to within the threshold period.
                </p>
              </div>
            )}

            {unansweredChats.length > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <p style={{ margin: 0, color: '#6b7280' }}>
                    Found {unansweredChats.length} chat{unansweredChats.length !== 1 ? 's' : ''} with unanswered questions
                  </p>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {triggeredChatIds.size > 0 && (
                    <button
                      onClick={refreshTriggeredMessages}
                      disabled={refreshingMessages}
                      title="Refresh messages for all AI-triggered chats"
                      style={{
                        padding: '4px 12px',
                        fontSize: '0.85rem',
                        background: refreshingMessages ? '#d1fae5' : '#ecfdf5',
                        border: '1px solid #10b981',
                        borderRadius: '4px',
                        cursor: refreshingMessages ? 'default' : 'pointer',
                        color: '#065f46',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {refreshingMessages ? '⏳ Refreshing…' : '🔄 Refresh Messages'}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const lines: string[] = [
                        `Unanswered Messages Report`,
                        `Generated: ${new Date().toLocaleString()}`,
                        `Total: ${unansweredChats.length} chat${unansweredChats.length !== 1 ? 's' : ''}`,
                        '',
                        '─'.repeat(50),
                      ];
                      unansweredChats.forEach((chat, i) => {
                        lines.push('');
                        lines.push(`${i + 1}. ${chat.chatName || chat.chatId.split('@')[0]}`);
                        lines.push(`   Phone: ${chat.phoneNumber || chat.chatId.split('@')[0]}`);
                        if (chat.questions && chat.questions.length > 0) {
                          lines.push(`   Unanswered question${chat.questions.length > 1 ? 's' : ''}:`);
                          chat.questions.forEach((q: string) => {
                            lines.push(`   • ${q}`);
                          });
                        }
                      });
                      navigator.clipboard.writeText(lines.join('\n'));
                    }}
                    title="Copy results to clipboard"
                    style={{
                      padding: '4px 12px',
                      fontSize: '0.85rem',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    📋 Copy
                  </button>
                  </div>
                </div>

                {/* Select All / Deselect All */}
                <div style={{ marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.95rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedChatIds.size > 0 && selectedChatIds.size === unansweredChats.filter(c => !triggeredChatIds.has(c.chatId)).length}
                      ref={el => { if (el) el.indeterminate = selectedChatIds.size > 0 && selectedChatIds.size < unansweredChats.filter(c => !triggeredChatIds.has(c.chatId)).length; }}
                      onChange={toggleAllChats}
                      style={{ marginRight: '0.5rem', width: '18px', height: '18px' }}
                    />
                    <strong>
                      {selectedChatIds.size > 0 ? `Deselect All (${selectedChatIds.size} selected)` : 'Select All'}
                    </strong>
                  </label>
                  {triggeredChatIds.size > 0 && (
                    <span style={{ fontSize: '0.8rem', color: '#10b981' }}>
                      ✅ {triggeredChatIds.size} already triggered
                    </span>
                  )}
                </div>

                {/* Chat list */}
                <div style={{ maxHeight: '60vh', overflowY: 'auto', marginBottom: '1rem' }}>
                  {unansweredChats.map((chat) => {
                    const isTriggered = triggeredChatIds.has(chat.chatId);
                    const isSelected = selectedChatIds.has(chat.chatId);
                    const isCollapsed = collapsedChatIds.has(chat.chatId);
                    const chatMessages = chatMessagesCache[chat.chatId] || [];
                    const isLoadingMessages = loadingMessagesChatId === chat.chatId;
                    return (
                      <div
                        key={chat.chatId}
                        style={{
                          border: isTriggered ? '2px solid #10b981' : isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                          borderRadius: '6px',
                          padding: '0.75rem',
                          marginBottom: '0.75rem',
                          backgroundColor: isTriggered ? '#f0fdf4' : isSelected ? '#eff6ff' : 'white',
                          opacity: isTriggered ? 0.9 : 1
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                          {/* Checkbox / triggered icon */}
                          <div style={{ marginRight: '0.75rem', marginTop: '2px', cursor: isTriggered ? 'default' : 'pointer' }}
                            onClick={() => !isTriggered && toggleChatSelection(chat.chatId)}>
                            {isTriggered ? (
                              <span style={{ fontSize: '1.1rem' }}>✅</span>
                            ) : (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleChatSelection(chat.chatId)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                            )}
                          </div>
                          {/* Name + phone */}
                          <div style={{ flex: 1 }}>
                            <strong style={{ fontSize: '1rem' }}>
                              {chat.chatName || chat.chatId.split('@')[0]}
                              {isTriggered && <span style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 'normal', marginLeft: '0.5rem' }}>(AI triggered)</span>}
                            </strong>
                            <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.2rem 0 0 0' }}>
                              {chat.phoneNumber || chat.chatId.split('@')[0]}
                            </p>
                          </div>
                          {/* Collapse/expand toggle */}
                          <button
                            onClick={() => setCollapsedChatIds(prev => {
                              const next = new Set(prev);
                              if (next.has(chat.chatId)) next.delete(chat.chatId);
                              else next.add(chat.chatId);
                              return next;
                            })}
                            style={{
                              background: 'none', border: '1px solid #d1d5db', borderRadius: '4px',
                              padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer', color: '#6b7280',
                              marginLeft: '0.5rem', whiteSpace: 'nowrap'
                            }}
                          >
                            {isCollapsed ? '▼ Show' : '▲ Hide'}
                          </button>
                        </div>

                        {/* Messages + unanswered question — collapsed by default only when user hides */}
                        {!isCollapsed && (
                          <div style={{ marginTop: '0.5rem' }}>
                            {/* Conversation messages */}
                            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.5rem' }}>
                              {isLoadingMessages ? (
                                <p style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Loading messages…</p>
                              ) : chatMessages.length === 0 ? (
                                <p style={{ fontSize: '0.8rem', color: '#9ca3af', marginLeft: '2.5rem' }}>No recent messages.</p>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                  {chatMessages.map((msg: any, mIdx: number) => {
                                    const isOut = msg.from_me || msg.direction === 'outbound';
                                    const isAI = isOut && msg.sent_by && msg.sent_by !== 'human';
                                    const isUnansweredMsg = !isOut && chat.questions?.some((q: string) => msg.body && q.startsWith(msg.body.substring(0, 40)));
                                    return (
                                      <div key={mIdx} style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: isOut ? 'flex-end' : 'flex-start',
                                      }}>
                                        <div style={{
                                          maxWidth: '88%',
                                          background: isAI ? '#d1fae5' : isOut ? '#dbeafe' : isUnansweredMsg ? '#fef3c7' : '#f3f4f6',
                                          borderRadius: '8px',
                                          padding: '5px 10px',
                                          fontSize: '0.82rem',
                                          color: '#1f2937',
                                          border: isUnansweredMsg ? '1px solid #f59e0b' : 'none'
                                        }}>
                                          {isAI && <span style={{ fontSize: '0.7rem', color: '#059669', display: 'block', marginBottom: '2px', fontWeight: '600' }}>🤖 AI Agent</span>}
                                          {isOut && !isAI && <span style={{ fontSize: '0.7rem', color: '#2563eb', display: 'block', marginBottom: '2px', fontWeight: '600' }}>Staff</span>}
                                          <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.body || '(media)'}</span>
                                          <span style={{ fontSize: '0.65rem', color: '#9ca3af', marginLeft: '6px', display: 'inline-block', marginTop: '2px' }}>
                                            {new Date(msg.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })} {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {isTriggered && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '6px', gap: '8px' }}>
                                  <span style={{ fontSize: '0.75rem', color: '#10b981' }}>✅ AI triggered</span>
                                  <button
                                    onClick={() => loadChatMessages(chat.chatId, true)}
                                    style={{ fontSize: '0.75rem', padding: '2px 8px', background: '#f0fdf4', border: '1px solid #10b981', borderRadius: '4px', cursor: 'pointer', color: '#065f46' }}
                                  >
                                    🔄 Refresh
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Unanswered question highlight */}
                            {chat.questions && chat.questions.length > 0 && (
                              <div style={{ marginTop: '0.5rem', padding: '6px 10px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #f59e0b' }}>
                                <span style={{ fontSize: '0.8rem', color: '#92400e', fontWeight: '600' }}>❓ Unanswered: </span>
                                <span style={{ fontSize: '0.82rem', color: '#78350f' }}>
                                  {chat.questions[0].length > 300 ? chat.questions[0].substring(0, 300) + '...' : chat.questions[0]}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* AI Agent selection */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                    🤖 AI Agent to Respond:
                  </label>
                  {chatAgents.length === 0 ? (
                    <p style={{ color: '#dc2626', fontSize: '0.9rem', margin: 0 }}>
                      ⚠️ No AI agents found. Please configure an agent in settings first.
                    </p>
                  ) : (
                    <select
                      value={selectedChatAgentId}
                      onChange={(e) => setSelectedChatAgentId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                      disabled={respondingToChats}
                    >
                      <option value="">-- Select an AI Agent --</option>
                      {chatAgents.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Inline status message */}
                {respondStatusMessage && (
                  <div style={{
                    padding: '0.75rem',
                    borderRadius: '6px',
                    marginBottom: '1rem',
                    background: respondStatusMessage.startsWith('✅') ? '#f0fdf4' : respondStatusMessage.startsWith('❌') ? '#fef2f2' : '#fffbeb',
                    border: `1px solid ${respondStatusMessage.startsWith('✅') ? '#86efac' : respondStatusMessage.startsWith('❌') ? '#fca5a5' : '#fde68a'}`,
                    fontSize: '0.9rem',
                    color: '#374151'
                  }}>
                    {respondStatusMessage}
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowUnansweredModal(false)}
                    disabled={respondingToChats}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: respondingToChats ? 'not-allowed' : 'pointer',
                      opacity: respondingToChats ? 0.6 : 1
                    }}
                  >
                    Close
                  </button>
                  <button
                    onClick={respondToUnansweredChats}
                    disabled={selectedChatIds.size === 0 || !selectedChatAgentId || respondingToChats || chatAgents.length === 0}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: selectedChatIds.size > 0 && selectedChatAgentId && !respondingToChats ? '#10b981' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: selectedChatIds.size > 0 && selectedChatAgentId && !respondingToChats ? 'pointer' : 'not-allowed',
                      opacity: (selectedChatIds.size === 0 || !selectedChatAgentId || respondingToChats) ? 0.6 : 1
                    }}
                  >
                    {respondingToChats ? '⏳ Responding...' : `📤 Respond to ${selectedChatIds.size} Chat${selectedChatIds.size !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateGroupModal && (
        <div className="modal-overlay" onClick={() => setShowCreateGroupModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>👥 Create WhatsApp Group</h2>
              <button
                onClick={() => setShowCreateGroupModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '0.75rem', background: '#e3f2fd', borderRadius: '0.5rem', marginBottom: '1.5rem', border: '1px solid #2196f3' }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#1565c0' }}>
                <strong>Entity:</strong> {entities.find(e => e.id === selectedEntityId)?.name || selectedEntityId}
              </p>
            </div>

            {groupCreationError && (
              <div style={{ padding: '0.75rem', background: '#ffebee', borderRadius: '0.5rem', marginBottom: '1rem', border: '1px solid #f44336' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#c62828' }}>
                  ❌ {groupCreationError}
                </p>
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Group Name:
              </label>
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Enter group name"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '1rem'
                }}
                disabled={creatingGroup}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Participants:
              </label>
              <textarea
                value={newGroupParticipants}
                onChange={(e) => setNewGroupParticipants(e.target.value)}
                placeholder="Enter phone numbers (international format)&#10;+1234567890&#10;+0987654321&#10;&#10;Separate with commas or new lines"
                rows={6}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontFamily: 'monospace'
                }}
                disabled={creatingGroup}
              />
              <p style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                📞 Use international format (e.g., +1234567890). Separate multiple numbers with commas or new lines.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCreateGroupModal(false)}
                disabled={creatingGroup}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: creatingGroup ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  opacity: creatingGroup ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateGroup}
                disabled={creatingGroup || !newGroupName.trim() || !newGroupParticipants.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: (creatingGroup || !newGroupName.trim() || !newGroupParticipants.trim()) ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: (creatingGroup || !newGroupName.trim() || !newGroupParticipants.trim()) ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                {creatingGroup ? '⏳ Creating...' : '✅ Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Lead Classification Modal */}
    {showClassifyModal && (
      <div className="modal-overlay" onClick={() => setShowClassifyModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '960px', width: '95vw' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>🏷️ Lead Classification</h2>
            <button
              onClick={() => setShowClassifyModal(false)}
              style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
            >×</button>
          </div>

          {/* Date range selector */}
          <div style={{
            background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '0.5rem',
            padding: '0.875rem 1rem', marginBottom: '1rem',
            display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap'
          }}>
            <span style={{ fontWeight: 500, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>Date range:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                type="datetime-local"
                value={classifyStartDate}
                onChange={(e) => setClassifyStartDate(e.target.value)}
                disabled={classifyLoading}
                style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.875rem' }}
              />
              <span style={{ color: '#666' }}>→</span>
              <input
                type="datetime-local"
                value={classifyEndDate}
                onChange={(e) => setClassifyEndDate(e.target.value)}
                disabled={classifyLoading}
                style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.875rem' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', marginLeft: 'auto' }}>
              {[
                { label: '24h', hours: 24 },
                { label: '48h', hours: 48 },
                { label: '72h', hours: 72 },
                { label: '7d', hours: 168 },
              ].map(({ label, hours }) => (
                <button
                  key={label}
                  onClick={() => {
                    const end = new Date();
                    const start = new Date(end.getTime() - hours * 60 * 60 * 1000);
                    setClassifyEndDate(toLocalDatetimeStr(end));
                    setClassifyStartDate(toLocalDatetimeStr(start));
                  }}
                  disabled={classifyLoading}
                  style={{
                    padding: '3px 10px', fontSize: '0.8rem', border: '1px solid #bbb',
                    borderRadius: '4px', background: 'white', cursor: classifyLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={runClassification}
              disabled={classifyLoading || !classifyStartDate || !classifyEndDate}
              style={{
                padding: '5px 16px', background: classifyLoading ? '#ccc' : '#1565c0', color: 'white',
                border: 'none', borderRadius: '4px', cursor: classifyLoading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap'
              }}
            >
              {classifyLoading ? '⏳ Running...' : '▶ Run'}
            </button>
          </div>

          {/* Loading state */}
          {classifyLoading && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="spinner" style={{ fontSize: '2rem', display: 'inline-block', marginBottom: '0.5rem' }}>⏳</div>
              <p style={{ color: '#666' }}>Analyzing conversations...</p>
            </div>
          )}

          {/* Save status */}
          {classifySaveStatus && (
            <div style={{
              padding: '0.75rem',
              background: classifySaveStatus.failed === 0 ? '#e8f5e9' : '#fff3e0',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              border: `1px solid ${classifySaveStatus.failed === 0 ? '#4caf50' : '#ff9800'}`
            }}>
              ✅ Saved {classifySaveStatus.saved} to CRM
              {classifySaveStatus.failed > 0 && ` • ⚠️ ${classifySaveStatus.failed} failed`}
              {classifySaveStatus.errors.length > 0 && (
                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                  {classifySaveStatus.errors.slice(0, 3).join(' | ')}
                </div>
              )}
            </div>
          )}

          {/* Per-chat errors from run */}
          {classifyRunErrors.length > 0 && (
            <div style={{
              padding: '0.75rem', background: '#fff3e0', borderRadius: '0.5rem',
              marginBottom: '1rem', border: '1px solid #ff9800', fontSize: '0.85rem'
            }}>
              <strong>⚠️ {classifyRunErrors.length} chat{classifyRunErrors.length !== 1 ? 's' : ''} failed:</strong>
              <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
                {classifyRunErrors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                {classifyRunErrors.length > 5 && <li>…and {classifyRunErrors.length - 5} more</li>}
              </ul>
            </div>
          )}

          {/* Results table */}
          {!classifyLoading && classifyResults.length > 0 && (
            <>
              <p style={{ color: '#666', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                {classifyResults.length} chat{classifyResults.length !== 1 ? 's' : ''} classified
              </p>
              <div style={{ overflowX: 'auto', maxHeight: '50vh', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', whiteSpace: 'nowrap' }}>Phone / LID</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Summary</th>
                      <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #ddd', whiteSpace: 'nowrap' }}>Confidence</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Classification</th>
                      <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>CRM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classifyResults.map(result => {
                      const conf = result.confidence_pct;
                      const confColor = conf >= 67 ? '#2e7d32' : conf >= 34 ? '#e65100' : '#b71c1c';
                      const confBg = conf >= 67 ? '#e8f5e9' : conf >= 34 ? '#fff3e0' : '#ffebee';
                      const currentClassification = classifyOverrides[result.id] ?? result.classification;
                      return (
                        <tr key={result.id} style={{ borderBottom: '1px solid #eee', background: result.saved_to_crm ? '#f0fff4' : 'white' }}>
                          <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                            <div style={{ fontWeight: 500 }}>{result.phone_number}</div>
                            {result.zoho_lead_id && (
                              <div style={{ fontSize: '0.75rem', color: '#999' }}>LID: {result.zoho_lead_id}</div>
                            )}
                          </td>
                          <td style={{ padding: '8px', maxWidth: '300px' }}>
                            <div title={result.reasoning}>{result.summary}</div>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <span style={{
                              background: confBg, color: confColor,
                              padding: '2px 8px', borderRadius: '12px', fontWeight: 600,
                              fontSize: '0.8rem', whiteSpace: 'nowrap'
                            }}>
                              {conf}%
                            </span>
                          </td>
                          <td style={{ padding: '8px' }}>
                            <select
                              value={currentClassification}
                              onChange={(e) => setClassifyOverrides(prev => ({ ...prev, [result.id]: e.target.value }))}
                              style={{
                                padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px',
                                fontSize: '0.85rem', width: '100%', cursor: 'pointer'
                              }}
                              disabled={result.saved_to_crm}
                            >
                              {CLASSIFY_STATUSES.map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            {result.saved_to_crm ? (
                              <span style={{ color: '#2e7d32', fontWeight: 600 }}>✅</span>
                            ) : result.zoho_lead_id ? (
                              <span style={{ color: '#999', fontSize: '0.75rem' }}>pending</span>
                            ) : (
                              <span style={{ color: '#ccc', fontSize: '0.75rem' }}>no LID</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Empty / pre-run state */}
          {!classifyLoading && classifyResults.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              {classifyHasRun
                ? 'No unclassified chats with activity in this date range.'
                : 'Set a date range and click ▶ Run to classify leads.'}
            </div>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowClassifyModal(false)}
              style={{
                padding: '0.5rem 1.25rem', background: '#f5f5f5', color: '#333',
                border: '1px solid #ddd', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.9rem'
              }}
            >
              Close
            </button>
            <button
              onClick={handleSaveToCrm}
              disabled={savingToCrm || classifyLoading || classifyResults.length === 0 || classifyResults.every(r => !r.zoho_lead_id || r.saved_to_crm)}
              style={{
                padding: '0.5rem 1.25rem',
                background: (savingToCrm || classifyLoading || classifyResults.length === 0 || classifyResults.every(r => !r.zoho_lead_id || r.saved_to_crm)) ? '#ccc' : '#1976d2',
                color: 'white', border: 'none', borderRadius: '0.5rem',
                cursor: (savingToCrm || classifyLoading || classifyResults.length === 0 || classifyResults.every(r => !r.zoho_lead_id || r.saved_to_crm)) ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem', fontWeight: 500
              }}
            >
              {savingToCrm ? '⏳ Saving...' : '💾 Save to CRM'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Inline styles for spinner animation */}
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .spinner {
        animation: spin 1s linear infinite;
      }
    `}</style>
  </>
  );
};

export default ChatBoard;
